import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { triggerSessionExpired } from '../contexts/SessionContext';

// API response types (inline to avoid build issues)
interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: { field: string; message: string }[] | Record<string, unknown>;
  };
}

// Enhanced error info type
export interface ErrorInfo {
  message: string;
  code: string;
  details?: { field: string; message: string }[] | Record<string, unknown> | null;
  endpoint?: string;
  method?: string;
  payload?: unknown;
}

// Get API URL from environment variable or use relative path for development
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for refresh token
});

// Token storage (in memory for security)
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // If 401 and not already retried, try to refresh token
    if (
      error.response?.status === 401 &&
      error.response?.data?.error?.code === 'TOKEN_EXPIRED' &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      
      try {
        const response = await api.post('/auth/refresh');
        const newToken = response.data.data.tokens.accessToken;
        setAccessToken(newToken);
        
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear token and show session expired modal
        setAccessToken(null);
        triggerSessionExpired();
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// User-friendly error messages for common scenarios
const USER_FRIENDLY_ERRORS: Record<string, string> = {
  CONTRACTS_NOT_ENABLED: 'Contracts feature is not enabled for your organization. Please contact support to enable it.',
  FEATURE_NOT_AVAILABLE: 'This feature is not available yet. It may be coming soon!',
  UNAUTHORIZED: 'You need to be logged in to access this feature.',
  FORBIDDEN: 'You don\'t have permission to access this feature.',
  NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection.',
};

// Helper function to extract error message
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    const apiError = axiosError.response?.data;
    const status = axiosError.response?.status;
    const url = axiosError.config?.url || '';

    // Check for API error response first
    if (apiError?.error?.message) {
      // Map known error codes to friendly messages
      const code = apiError.error.code;
      if (code && USER_FRIENDLY_ERRORS[code]) {
        return USER_FRIENDLY_ERRORS[code];
      }
      return apiError.error.message;
    }

    // Handle 404 errors - likely route not deployed or feature not enabled
    if (status === 404) {
      // Check if it's a contracts-related endpoint
      if (url.includes('/contracts') || url.includes('/contract-templates')) {
        return 'Contracts feature is not available yet. Please check back later or contact support.';
      }
      return 'The requested resource was not found. This feature may not be available yet.';
    }

    // Handle network errors (no response received)
    if (axiosError.request && !axiosError.response) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }

    // Handle server errors
    if (status && status >= 500) {
      return 'Something went wrong on our end. Please try again later.';
    }

    // Handle auth errors
    if (status === 401) {
      return 'You need to be logged in to access this feature.';
    }
    if (status === 403) {
      return 'You don\'t have permission to access this feature.';
    }

    return error.message || 'An unexpected error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

// Helper to get full error info for error reporting
export function getErrorInfo(error: unknown): ErrorInfo {
  const defaultInfo: ErrorInfo = {
    message: 'An unexpected error occurred',
    code: 'UNKNOWN_ERROR',
    details: null,
  };

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    const data = axiosError.response?.data;
    const status = axiosError.response?.status;

    if (data?.error) {
      defaultInfo.message = data.error.message || defaultInfo.message;
      defaultInfo.code = data.error.code || defaultInfo.code;
      defaultInfo.details = data.error.details;
    } else if (status === 404) {
      // 404 - Route not found (likely API misconfiguration)
      defaultInfo.message = `Route ${axiosError.config?.method?.toUpperCase()} ${axiosError.config?.url} not found`;
      defaultInfo.code = 'NOT_FOUND';
    } else if (axiosError.request && !axiosError.response) {
      // Request made but no response
      defaultInfo.message = 'Unable to connect to server. Please check your internet connection.';
      defaultInfo.code = 'NETWORK_ERROR';
    } else if (status && status >= 500) {
      // Server errors
      defaultInfo.message = `Server error (${status}). Please try again later.`;
      defaultInfo.code = `SERVER_ERROR_${status}`;
    }

    // Add request info
    defaultInfo.endpoint = axiosError.config?.url;
    defaultInfo.method = axiosError.config?.method?.toUpperCase();
    defaultInfo.payload = axiosError.config?.data ? JSON.parse(axiosError.config.data) : undefined;
  } else if (error instanceof Error) {
    defaultInfo.message = error.message;
  }

  return defaultInfo;
}

// Auth API functions
export const authApi = {
  register: async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName: string;
    organizationSlug: string;
    serviceTypeId: string;
  }) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },
  
  login: async (data: {
    email: string;
    password: string;
    fingerprint?: string;
    deviceToken?: string;
  }) => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },
  
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
  
  refresh: async () => {
    const response = await api.post('/auth/refresh');
    return response.data;
  },
  
  me: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  checkSlug: async (slug: string) => {
    const response = await api.get(`/auth/check-slug/${slug}`);
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, password: string) => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },
};

// Health check
export const healthApi = {
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Service Types API (public - for registration dropdown)
export interface PublicServiceType {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
}

export const serviceTypesApi = {
  /**
   * List all active service types (public endpoint, no auth required)
   */
  list: async (): Promise<{ serviceTypes: PublicServiceType[] }> => {
    const response = await api.get('/service-types');
    return response.data.data;
  },

  /**
   * Create a custom service type (public endpoint for registration)
   * If service type already exists, returns the existing one
   */
  create: async (name: string): Promise<{ serviceType: PublicServiceType; isNew: boolean }> => {
    const response = await api.post('/service-types', { name });
    return response.data.data;
  },
};
